import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import Button from "components/Button";
import COLORS from "shared/constants/colors";
import { showLoader, hideLoader } from "actions/loader";
import { MAX_UPLOAD_FILES, UPLOAD_TABS } from "pages/dtf-transfers/by-size/data";
import UploadedArtworkEditor from "pages/dtf-transfers/by-size/UploadedArtworkEditor";
import {
  createArtworkStateFromCloudinary,
  isImageFile,
  openCloudinaryUploadWidget,
  revokeArtworkPreview,
  uploadFileToCloudinary,
} from "pages/dtf-transfers/by-size/helpers";
import {
  UploadSection,
  UploadSectionHeader,
  UploadTabs,
  UploadTab,
  UploadPanel,
  DropZone,
  UploadIconCircle,
  DropZoneTitle,
  DropZoneSubtitle,
  PlaceholderPanel,
} from "styles/pages/dtf-transfers/by-size";

const UploadArtwork = () => {
  const dispatch = useDispatch();
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const [activeTab, setActiveTab] = useState("upload");
  const [isDragging, setIsDragging] = useState(false);
  const [artwork, setArtwork] = useState(null);

  useEffect(() => {
    return () => {
      revokeArtworkPreview(artwork);
    };
  }, [artwork]);

  const setUploadedArtwork = (cloudinaryInfo) => {
    if (!cloudinaryInfo?.secure_url) return;

    setArtwork((current) => {
      revokeArtworkPreview(current);
      return createArtworkStateFromCloudinary(cloudinaryInfo);
    });
  };

  const openCloudinaryWidget = () => {
    if (!emailid) return;

    openCloudinaryUploadWidget(emailid, setUploadedArtwork);
  };

  const uploadDroppedFiles = async (selectedFiles) => {
    if (!emailid) return;

    const nextFiles = Array.from(selectedFiles || []).slice(0, MAX_UPLOAD_FILES);
    const imageFile = nextFiles.find(isImageFile);
    if (!imageFile) return;

    try {
      dispatch(showLoader());
      const cloudinaryInfo = await uploadFileToCloudinary(imageFile, emailid);
      setUploadedArtwork(cloudinaryInfo);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleBrowseClick = (event) => {
    event.stopPropagation();
    openCloudinaryWidget();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    uploadDroppedFiles(event.dataTransfer.files);
  };

  const handleRemoveArtwork = () => {
    setArtwork((current) => {
      revokeArtworkPreview(current);
      return null;
    });
  };

  const renderUploadPanel = () => (
    <DropZone
      $isDragging={isDragging}
      onClick={openCloudinaryWidget}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      <UploadIconCircle>
        <CloudUploadOutlinedIcon />
      </UploadIconCircle>
      <DropZoneTitle>
        Upload your files (up to {MAX_UPLOAD_FILES} MB)
      </DropZoneTitle>
      <DropZoneSubtitle>
        Drag and drop files here or click to browse
      </DropZoneSubtitle>
      <Button
        title="Browse"
        onClick={handleBrowseClick}
        containerStyle={{
          backgroundColor: COLORS.BLUE,
          color: COLORS.WHITE,
          textTransform: "none",
          borderRadius: "6px",
          padding: "8px 28px",
        }}
      />
    </DropZone>
  );

  const renderTabContent = () => {
    if (activeTab === "upload") {
      if (artwork) {
        return (
          <UploadedArtworkEditor
            artwork={artwork}
            onChange={setArtwork}
            onRemove={handleRemoveArtwork}
          />
        );
      }

      return renderUploadPanel();
    }

    const tab = UPLOAD_TABS.find((item) => item.id === activeTab);
    return <PlaceholderPanel>{tab?.label} coming soon.</PlaceholderPanel>;
  };

  return (
    <UploadSection>
      <UploadSectionHeader>
        <h2>Upload Your Artwork</h2>
        <UploadTabs>
          {UPLOAD_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <UploadTab
                key={tab.id}
                type="button"
                $active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon />
                {tab.label}
              </UploadTab>
            );
          })}
        </UploadTabs>
      </UploadSectionHeader>

      <UploadPanel>{renderTabContent()}</UploadPanel>
    </UploadSection>
  );
};

export default UploadArtwork;
